import React from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTriangleExclamation } from '@fortawesome/pro-light-svg-icons/faTriangleExclamation';
import type { XPostsUsage } from 'fym-shared/src/music24/xPosts';
import { TypedMemo } from '../../../common/types';
import { xMessages } from '../xMessages';
import { formatXResetDate, isXPostsFull } from '../xPostsLimit';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole Promote pillar; each part uses its own slice of it */
import shared from '../Promote.module.scss';
/* eslint-enable css-modules/no-unused-class */

/** What the block says under the amber line at the limit. */
export type XPostsFullNote = 'channels' | 'draft' | 'none';

interface ChannelXPostsProps {
  usage: XPostsUsage;
  /**
   * The note at the limit: `channels` says that new X posts stay drafts (the Channels card),
   * `draft` says that the post can wait as a draft (the Composer), `none` says nothing more.
   */
  fullNote?: XPostsFullNote;
  /** Hides the amber line (the Composer draft with no time shows the note only, design c2). */
  hideFullLine?: boolean;
}

/**
 * The API posts of one X account this month: "{used} of {limit} X posts this month" and a thin
 * meter. At the limit the meter turns amber and a line says when the count resets (design
 * x-connector b1, b2, c2, c3).
 */
const ChannelXPostsComponent: React.FC<ChannelXPostsProps> = ({ usage, fullNote = 'none', hideFullLine = false }) => {
  const intl = useIntl();
  const isFull = isXPostsFull(usage);
  const percent = usage.limit <= 0 ? 100 : Math.min(100, Math.round((usage.used / usage.limit) * 100));

  return (
    <div className={clsx(shared.xPosts, isFull && shared.xPostsFull)} data-testid="x-posts">
      <span className={shared.xPostsRow}>
        {intl.formatMessage(xMessages.posts, { used: usage.used, limit: usage.limit })}
      </span>
      <span className={shared.xPostsMeter} aria-hidden="true">
        <span className={shared.xPostsMeterFill} style={{ width: `${percent}%` }} />
      </span>
      {isFull && !hideFullLine && usage.resetsAt !== '' ? (
        <div className={clsx(shared.stateLine, shared.stateLineAmber)} data-testid="x-posts-full">
          <FontAwesomeIcon icon={faTriangleExclamation} className={shared.stateLineIcon} />
          <span className={shared.stateLineText}>
            {intl.formatMessage(xMessages.postsFull, { date: formatXResetDate(intl, usage.resetsAt) })}
          </span>
        </div>
      ) : null}
      {isFull && fullNote === 'channels' ? (
        <p className={shared.stateLineDetail}>{intl.formatMessage(xMessages.postsFullHint)}</p>
      ) : null}
      {isFull && fullNote === 'draft' ? (
        <p className={shared.stateLineDetail}>{intl.formatMessage(xMessages.draftHint)}</p>
      ) : null}
    </div>
  );
};

export const ChannelXPosts = TypedMemo(ChannelXPostsComponent);
